/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { IconButton, Typography, Box, Tooltip, CircularProgress, Link, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { deleteCompany, updateCompanies } from "./companiesFunctions";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Company, Effect, Nullable } from "../../utils/Interfaces";
import DataTable from "../../components/table/DataTable";
import ConfirmDeleteModal from "../../components/confirmation/ConfirmDeleteModal";
import { selectExistingCompanies, selectExistingCompaniesLoaded } from "./companiesSlice";
import CreateCompanyModal from "./CreateCompanyModal";
import EditCompanyModal from "./EditCompanyModal";
import { useWindowDimensions } from "../../utils/Hooks";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

const CompaniesList = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { height, width } = useWindowDimensions();

    const existingCompanies: Company[] = useAppSelector(selectExistingCompanies);
    const existingCompaniesLoaded: boolean = useAppSelector(selectExistingCompaniesLoaded);

    const dispatch = useAppDispatch();

    const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [selectedCompany, setSelectedCompany] = useState<Nullable<Company>>(null);

    const navigate = useNavigate();

    const columns: GridColDef<any, any, any>[] = [
        { field: "id", headerName: "id", filterable: false, sortable: true, width: 200 },
        {
            field: "name",
            headerName: "Company Name",
            sortable: true,
            width: 300,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.name;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Link style={{ cursor: "pointer" }} onClick={() => navigate(`/companies/${params.row.id}`)}>
                        <Typography variant="overline" fontSize={14}>
                            {params.row.name}
                        </Typography>
                    </Link>
                );
            },
        },
        {
            field: "edit",
            headerName: "Edit",
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title="Edit Company">
                        <IconButton
                            onClick={() => {
                                setSelectedCompany(params.row);
                                setEditModalOpen(true);
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "delete",
            headerName: "Delete",
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Box>
                        {params.row.deletable && (
                            <Tooltip title="Delete Company">
                                <IconButton
                                    onClick={() => {
                                        setSelectedCompany(params.row);
                                        setDeleteModalOpen(true);
                                    }}
                                >
                                    <DeleteIcon color="error" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                );
            },
        },
    ];

    useEffect(() => {
        updateCompanies(dispatch, () => {});
    }, [dispatch]);

    const onClickAddNew = () => setCreateModalOpen(true);

    const handleClose = () => setCreateModalOpen(false);

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Service", route: "/?section=office" },
                    { text: "Companies", route: "/companies" },
                ]}
            />

            {!existingCompaniesLoaded && (
                <Stack direction="row" justifyContent="center" width="100%" height={height / 2 + 100}>
                    <Stack direction="column" justifyContent="center">
                        <CircularProgress size={100} />
                    </Stack>
                </Stack>
            )}

            {existingCompaniesLoaded && (
                <DataTable
                    tableName="COMPANIES_LIST"
                    height={height - 170}
                    columns={columns}
                    data={existingCompanies}
                    allowRowSelected={false}
                >
                    <Button onClick={onClickAddNew}>Add new Company</Button>
                </DataTable>
            )}

            {createModalOpen && (
                <CreateCompanyModal
                    open={createModalOpen}
                    handleClose={handleClose}
                    onCompanyCreated={() => {
                        setCreateModalOpen(false);
                        updateCompanies(dispatch, () => {});
                    }}
                />
            )}

            {editModalOpen && (
                <EditCompanyModal
                    selectedCompany={selectedCompany as Company}
                    open={editModalOpen}
                    handleClose={() => {
                        setEditModalOpen(false);
                    }}
                    onCompanyModified={() => {
                        setEditModalOpen(false);
                        updateCompanies(dispatch, () => {});
                    }}
                />
            )}

            {deleteModalOpen && (
                <ConfirmDeleteModal
                    confirmText={`delete ${selectedCompany?.name}`}
                    description="You are about to delete the following company, all of its sites, work orders trips and assignment data."
                    handleClose={() => setDeleteModalOpen(false)}
                    open={deleteModalOpen}
                    onDelete={(modalCallback: Effect) => {
                        deleteCompany(selectedCompany?.id as number, dispatch, () => {
                            setDeleteModalOpen(false);
                            updateCompanies(dispatch, () => {});
                            modalCallback();
                        });
                    }}
                />
            )}
        </Box>
    );
};

export default CompaniesList;
